import {
  Checkbox,
  CheckboxInput,
  dialog,
  Field,
  Form,
  formDialog,
  IconButtonPreventDefault,
  Paper,
  Section,
  SubmitButton,
  SummaryList,
  Title,
  Toolbar,
  yesNo,
} from "ui";
import {
  Accordion,
  AccordionSummary,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  addNewList,
  addReplaceListItem,
  deleteList,
  exportLists,
  permanentlyDeleteList,
  recoverList,
  removeReplaceListItem,
  setActiveReplaceLists,
  StoredReplaceList,
  useReplaceList,
} from "./replace-list-store";
import { Add, Delete, ExpandMore, Restore } from "@mui/icons-material";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ImportReplaceListButton from "./ImportReplaceLists";

const setActiveLists: Dispatch<SetStateAction<StoredReplaceList[]>> = (
  value
) => {
  if (typeof value === "function") {
    const { activeLists } = useReplaceList.getState();
    setActiveReplaceLists(value(activeLists));
  } else {
    setActiveReplaceLists(value);
  }
};

export function RemovedLists() {
  const { t } = useTranslation();
  const removedLists = useReplaceList((state) => state.removedLists);
  const showRemovedLists = useReplaceList((state) => state.showRemovedLists);
  const [search, setSearch] = useState("");

  return (
    <Paper item xs={12} md={12} lg={6}>
      <Accordion
        expanded={showRemovedLists}
        onChange={(_e, expanded) =>
          useReplaceList.setState({ showRemovedLists: expanded })
        }
        disableGutters
        sx={{ margin: 0, padding: 0 }}
      >
        <Toolbar>
          <Section>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Title>{t("Removed replace lists")}</Title>
            </AccordionSummary>
          </Section>
          <Section>{t("Automatically removed after ~10 days")}</Section>
        </Toolbar>
        <SummaryList
          backgroundColor="inherit"
          data={removedLists}
          pageSize={10}
          searchString={search}
          setSearchString={setSearch}
          search={(row, text) =>
            row.name.toLowerCase().includes(text.toLowerCase())
          }
          columns={[
            {
              label: "Name",
              getValue(row) {
                return row.name;
              },
            },
            {
              label: "",
              width: "auto",
              getValue(row) {
                return (
                  <IconButton
                    title="Recover"
                    color="primary"
                    onClick={() => recoverList(row._id)}
                  >
                    <Restore />
                  </IconButton>
                );
              },
            },
            {
              label: "",
              width: "auto",
              getValue(row) {
                return (
                  <IconButtonPreventDefault
                    color="error"
                    onClick={async () => {
                      const confirm = await yesNo(
                        `Do you want to permanently delete [${row.name}]?`
                      );

                      if (confirm) {
                        permanentlyDeleteList(row._id);
                      }
                    }}
                  >
                    <Delete />
                  </IconButtonPreventDefault>
                );
              },
            },
          ]}
        />
      </Accordion>
    </Paper>
  );
}

export function ReplaceLists() {
  const { t } = useTranslation();
  const lists = useReplaceList((state) => state.lists);
  const activeLists = useReplaceList((state) => state.activeLists);
  const showRemovedLists = useReplaceList((state) => state.showRemovedLists);
  const [search, setSearch] = useState("");

  return (
    <Paper item xs={12} md={12} lg={6}>
      <Toolbar>
        <Section>
          <Title>{t("Replace lists")}</Title>
        </Section>
        <Section>
          {/* <Button
            color="inherit"
            onClick={async () => {
              await syncQuery.query("clearAllReplaceLists");
              useReplaceList.setState({
                active: {},
                activeLists: [] as StoredReplaceList[],
                lists: [] as StoredReplaceList[],
                editList: "",
              });
            }}
          >
            Clear all
          </Button> */}
          <Button
            color="inherit"
            onClick={async () => {
              const { name } = await formDialog(
                (resolve, reject) => (
                  <>
                    <DialogTitle>Choose a name</DialogTitle>
                    <DialogContent>
                      <Field name="name" placeholder="Name" />
                    </DialogContent>
                    <DialogActions>
                      <SubmitButton label="Create" />
                      <Button onClick={() => reject()}>Cancel</Button>
                    </DialogActions>
                  </>
                ),
                {
                  initialValues: { name: "" },
                }
              );

              if (name) {
                addNewList(name);
              }
            }}
          >
            {t("New")}
            <Add />
          </Button>
          <ImportReplaceListButton />
          <Button
            color="inherit"
            onClick={() => {
              exportLists();
            }}
          >
            Export all
          </Button>
        </Section>
      </Toolbar>
      <SummaryList
        onClick={(list) => useReplaceList.setState({ editList: list._id })}
        selected={activeLists}
        setSelected={setActiveLists}
        backgroundColor="inherit"
        data={lists}
        pageSize={10}
        searchString={search}
        setSearchString={setSearch}
        search={(row, text) =>
          row.name.toLowerCase().includes(text.toLowerCase())
        }
        columns={[
          {
            label: "Name",
            getValue(row) {
              return row.name;
            },
          },
          {
            label: "",
            width: "auto",
            getValue(row) {
              return (
                <IconButtonPreventDefault
                  color="error"
                  onClick={async () => {
                    const confirm = await yesNo(
                      `Do you want to delete [${row.name}]?`
                    );

                    if (confirm) {
                      deleteList(row._id);
                    }
                  }}
                >
                  <Delete />
                </IconButtonPreventDefault>
              );
            },
          },
        ]}
      />
    </Paper>
  );
}

export function EditList() {
  const editListId = useReplaceList((state) => state.editList);
  const lists = useReplaceList((state) => state.lists);
  const editList = useMemo(
    () => lists.find((list) => list._id === editListId),
    [editListId, lists]
  );

  if (!editList) return null;

  return <EditListContent editList={editList} />;
}

export function EditListContent({ editList }: { editList: StoredReplaceList }) {
  const { t } = useTranslation();
  const listRows = useMemo(
    () =>
      Object.entries(editList.replacements).map(([from, to]) => ({
        _id: from,
        from,
        to,
      })),
    [editList]
  );
  const [search, setSearch] = useState("");
  const [forceRemove, setForceRemove] = useState(false);
  useEffect(() => {
    setForceRemove(false);
  }, [editList._id]);

  const onRemoveReplaceItem = useCallback(
    async (row: (typeof listRows)[number]) => {
      if (forceRemove) {
        removeReplaceListItem(editList._id, row.from);
        return;
      }

      const force = await dialog<boolean, undefined>((resolve, reject) => (
        <>
          <DialogTitle>Delete [{row.from}]</DialogTitle>
          <DialogContent>
            <Form
              initialValues={{ force: false }}
              onSubmit={({ force }) => {
                resolve(force);
              }}
            >
              <Checkbox name="force" label="Don't ask again" />
              <Typography>
                Only valid until you edit a different list or close/change
                settings screen
              </Typography>
              <SubmitButton label="Ja" />
              <Button onClick={() => reject()}>Nej</Button>
            </Form>
          </DialogContent>
        </>
      ));

      removeReplaceListItem(editList._id, row.from);
      if (force) setForceRemove(true);
    },
    [forceRemove, editList]
  );

  return (
    <Paper item xs={12} md={12} lg={6}>
      <Toolbar>
        <Title>{t("Edit list") + `: ${editList.name}`}</Title>
        <Button
          color="inherit"
          onClick={async () => {
            const { from, to } = await formDialog(
              (resolve, reject) => (
                <>
                  <DialogTitle>Add new replace item</DialogTitle>
                  <DialogContent>
                    <Field name="from" placeholder="From" autoFocus />
                    <Field name="to" placeholder="To" />
                  </DialogContent>
                  <DialogActions>
                    <SubmitButton label="Add" />
                    <Button onClick={() => reject()}>Cancel</Button>
                  </DialogActions>
                </>
              ),
              {
                initialValues: { from: "", to: "" },
              }
            );

            if (from) {
              addReplaceListItem(editList._id, from, to);
            }
          }}
        >
          Add
          <Add />
        </Button>
      </Toolbar>
      <SummaryList
        data={listRows}
        backgroundColor="inherit"
        pageSize={10}
        searchString={search}
        setSearchString={setSearch}
        search={(row, text) =>
          row.from.toLowerCase().includes(text.toLowerCase()) ||
          row.to.toLowerCase().includes(search.toLowerCase())
        }
        columns={[
          {
            label: "",
            width: "auto",
            minWidth: 50,
            getValue(row) {
              return (
                <IconButton
                  color="primary"
                  onClick={() => onRemoveReplaceItem(row)}
                >
                  <Delete />
                </IconButton>
              );
            },
          },
          {
            label: t("From"),
            getValue(row) {
              return row.from;
            },
          },
          {
            label: t("To"),
            getValue(row) {
              return row.to;
            },
          },
        ]}
      />
    </Paper>
  );
}
